// Dynamic generation of static completion terms for search functionality
// Terms are generated based on sales channel mappings

// Sales Channels IDs enum
const SC_ID = {
    A24: 'a24',
    AT: 'at',
    KCOM: 'kcom',
    TIRENDO: 'tirendo',
    OTC: 'otc',
    NL: 'nl',
    DK: 'dk',
    FR: 'fr',
};

const SCHEMA_BY_SCID = {
    'www-autoteile24-de': SC_ID.A24,
    'www-k24-at': SC_ID.AT,
    'www-kfzteile-com': SC_ID.KCOM,
    'www-carpardoo-nl': SC_ID.NL,
    'www-carpardoo-dk': SC_ID.DK,
    'www-carpardoo-fr': SC_ID.FR,
    'sis_tirendo-delticom-de': SC_ID.TIRENDO,
    'otc_branch1-kfzteile24-de': SC_ID.OTC,
    'otc_branch2-kfzteile24-de': SC_ID.OTC,
    'otc_branch3-kfzteile24-de': SC_ID.OTC,
};

const URL_PREFIXES = {
    [SC_ID.A24]: '/ersatzteile-verschleissteile/',
    [SC_ID.KCOM]: '/autoteile/',
    [SC_ID.AT]: '/kategorie/ersatzteile-verschleissteile/',
    [SC_ID.NL]: '/categorie/auto-onderdelen/',
    [SC_ID.DK]: '/categorie/reservedele/',
    [SC_ID.FR]: '/categorie/pieces-auto/',
    [SC_ID.TIRENDO]: '/kategorie/autoteile/',
    [SC_ID.OTC]: '/kategorie/autoteile/',
};

const BASE_TERMS = {
    402: { label: 'Bremsbeläge', urlSuffix: 'bremsanlage/bremsbelaege' },
    82: { label: 'Bremssattel', urlSuffix: 'bremsanlage/bremssattel/bremssattel-halter-traeger' },
    78: { label: 'Bremsscheiben', urlSuffix: 'bremsanlage/bremsscheiben' },
    273: { label: 'Lenker / Querlenker', urlSuffix: 'achsaufhaengung/lenker-querlenker/lenker-quer-laengs-schraeg' },
    8: { label: 'Luftfilter', urlSuffix: 'filter/luftfilter' },
    7: { label: 'Ölfilter', urlSuffix: 'filter/oelfilter' },
    654: { label: 'Radlagersatz', urlSuffix: 'achsaufhaengung/radnabe-radlager/radlager' },
};

const TERM_OVERRIDES = {
    [SC_ID.KCOM]: {
        402: { urlSuffix: 'bremsanlage/scheibenbremse/bremsbelag/-bremsbelagsatz-scheibenbremse' },
        82: { urlSuffix: 'bremsanlage/bremssattel/bremssattel-haltertraeger/-bremssattel' },
        78: { urlSuffix: 'bremsanlage/scheibenbremse/bremsscheibe/-bremsscheibe' },
        273: { urlSuffix: 'achsaufhaengungradfuehrungraeder/lenkerlenkerlagerung/lenker-quer-laengs-schraeg-/-lenker-radaufhaengung' },
        654: { urlSuffix: 'achsaufhaengungradfuehrungraeder/radnabe-lagerung/radlager/-radlagersatz' },
    },
    [SC_ID.NL]: {
        402: { label: 'Remblokken', urlSuffix: 'remmen/remblokken' },
        82: { label: 'Remklauwhouder', urlSuffix: 'remmen/remklauw/remklauw-houder' },
        78: { label: 'Remschijven', urlSuffix: 'remmen/remschijven' },
        273: { label: 'Stuurarmen', urlSuffix: 'stuur-onderdelen/stuurarmen' },
        8: { label: 'Luchtfilters', urlSuffix: 'filters/luchtfilters' },
        7: { label: 'Brandstoffilters', urlSuffix: 'filters/brandstoffilters' },
        654: { label: 'Wiellagers', urlSuffix: 'wielophanging/wielnaaf-wiellagers/wiellagers' },
    },
    [SC_ID.DK]: {
        402: { label: 'Bremseklodser', urlSuffix: 'bremsesystem/skivebremse/bremseklodser/-bremsebelaegningssaet-skivebremser' },
        82: { label: 'Bremsekaliper', urlSuffix: 'bremsesystem/bremsecalipre/bremsecaliper/-bremsekaliper' },
        78: { label: 'Bremseskive', urlSuffix: 'bremsesystem/skivebremse/bremseskive/-bremseskive' },
        273: { label: 'Ratstammearm', urlSuffix: 'styretojsdele/ratstammearm/-ratstammearm' },
        8: { label: 'Luftfilter', urlSuffix: 'filter/luftfilter/-luftfilter' },
        7: { label: 'Oliefilter', urlSuffix: 'filter/oliefilter/-oliefilter' },
        654: { label: 'Hjulleje', urlSuffix: 'styretoj-baerearme-hjulleje-stabilisator-mm/hjullejer-og-nav/hjulleje/-hjulleje' },
    },
    [SC_ID.FR]: {
        402: { label: 'Plaquettes de frein', urlSuffix: 'freinage/plaquettes-de-frein' },
        82: { label: "Étrier de frein / Support d'étriers", urlSuffix: 'freinage/etrier-de-frein/etrier-de-frein-support-d-etrier' },
        78: { label: 'Disques de frein', urlSuffix: 'freinage/disques-de-frein' },
        273: { label: 'Bras transversal / longitudinal / oscillant', urlSuffix: 'suspension/triangle-bras-de-suspension/bras-transversal-longitudinal-oscillant' },
        8: { label: 'Filtres à air', urlSuffix: 'filtre/filtres-a-air' },
        7: { label: 'Filtres à huile', urlSuffix: 'filtre/filtre-a-huile' },
        654: { label: 'Moyeux de roue', urlSuffix: 'suspension/moyeu-roulement-de-roue/roulement-de-roue' },
    },
};

export function getStaticCompletions(salesChannelId) {
    const schemaName = SCHEMA_BY_SCID[salesChannelId];

    if (!schemaName) return [];

    const prefix = URL_PREFIXES[schemaName] || '';
    const schemaOverrides = TERM_OVERRIDES[schemaName] || {};
    return Object.entries(BASE_TERMS).map(([genartKey, term]) => {
        const override = schemaOverrides[genartKey] || {};
        const label = override.label || term.label;
        const urlSuffix = override.urlSuffix || term.urlSuffix || '';
        return {
            genart: Number(genartKey),
            label,
            value: label,
            searchType: 'static',
            url: prefix + urlSuffix,
        };
    });
}
